import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { api, errorMessage } from '../../api/client';
import { MACHINES_KEY } from '../machines/MachineActions';
import { RECIPES_KEY } from '../RecipesPage';
import { WorkflowSummary } from './WorkflowSummary';

/** A friend's recipe, read-only, with "Copy to my workflows" (doc 30). */
export function SharedRecipePage() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const detail = useQuery({ queryKey: ['shared-recipe', id], queryFn: () => api.getSharedRecipe(id) });
  const machines = useQuery({ queryKey: MACHINES_KEY, queryFn: api.listMachines });
  const [machineOverride, setMachineOverride] = useState('');
  const list = machines.data?.machines ?? [];
  const machineId = machineOverride || list.find((m) => m.online)?.id || list[0]?.id || '';
  const copy = useMutation({
    mutationFn: () => api.copySharedRecipe(id, machineId ? { machineId } : {}),
    onSuccess: async ({ recipe }) => { await queryClient.invalidateQueries({ queryKey: RECIPES_KEY }); await navigate(`/workflows/${recipe.id}`); },
  });
  if (detail.isPending) return <p role="status">Loading…</p>;
  if (detail.isError) return <p role="alert" className="form-error">{errorMessage(detail.error)} <Link to="/workflows">Back to Workflows</Link></p>;
  const { recipe, workflow } = detail.data;
  return (
    <section aria-labelledby="shared-title">
      <p><Link to="/workflows">← Workflows</Link></p>
      <div className="page-head recipe-head">
        <h1 id="shared-title">{recipe.title}</h1>
        <span className="badge">Shared by {recipe.sharedBy.displayName}</span>
      </div>
      {recipe.intent && <p>{recipe.intent}</p>}
      {recipe.notes && <p className="muted">{recipe.notes}</p>}
      <div className="panel">
        <div className="machine-actions">
          {list.length > 0 && (
            <label>Run on{' '}
              <select value={machineId} onChange={(e) => { setMachineOverride(e.target.value); }}>
                {list.map((m) => <option key={m.id} value={m.id}>{m.name}{m.online ? '' : ' (offline)'}</option>)}
              </select>
            </label>
          )}
          <button type="button" disabled={copy.isPending} onClick={() => { copy.mutate(); }}>Copy to my workflows</button>
        </div>
        <p className="muted">The copy is yours: change it freely. Check it on your machine — it may use models you have not installed yet.</p>
        {copy.isError && <p role="alert" className="form-error">{errorMessage(copy.error)}</p>}
      </div>
      <section className="panel" aria-labelledby="shared-workflow-title">
        <h2 id="shared-workflow-title">Workflow</h2>
        <WorkflowSummary workflow={workflow} />
      </section>
    </section>
  );
}
